import React, { ReactNode } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { compose, withHandlers, withProps, withState } from 'recompose'
import styled from 'styled-components'

import { APP_STORE_URL, GOOGLE_PLAY_URL } from 'app-constants'
import AppStoreButton from 'assets/icons/app-store-white.svg'
import GooglePlayButton from 'assets/icons/google-play-white.svg'
import withAdBlocker from 'components/HOCs/withAdBlocker'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import { ZestyOverride } from 'components/HOCs/WithZestyOverrides'
import { OneTrustLink } from 'components/OneTrust/Link'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { isMobile } from 'components/Trackers/platform'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { style } from 'theme'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import { TestIDs } from '../../test/utils/testIds'
import Toast from './Toast'

type AppStoreButton = {
  image: React.Component
  href: string
  trackerId: string
  testId: string
  altText: string
}

type Props = {
  className?: string
  heroImage?: string
  children?: ReactNode
  header?: string
  text?: string
  pageNotFound?: boolean
  footnote?: string
  footnoteLock?: boolean
  fullWidth?: boolean
  fullHeight?: boolean
  onClose?: () => void
  showToast?: boolean
  handleToastDismiss?: () => void
  setShowToast?: () => void
  showAppStoreButtons?: boolean
  zestyOverride?: ZestyOverride
  isZestyOverride?: boolean
  hideNavOnMobileLandscape?: boolean
  mobileLandscapeSignaturePad?: boolean
  showOneTrustLink?: boolean
}

type AddedProps = {
  hideNav?: boolean
  showAdBlock?: boolean
}

type InnerProps = Props &
  AddedProps &
  RouteComponentProps &
  HeroTextProps &
  WithTieredRegistrationProps

export const LayoutContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-bottom: 20px;
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    padding-top: 10px;
  }
`

export const Container = styled.div`
  width: 800px;
  @media (max-width: ${style(theme => theme.breakpoints.md)}px) {
    width: 100%;
    padding: 0px 30px;
  }
  @supports (padding: max(0px)) and (padding: env(safe-area-inset-right)) {
    @media only screen and (min-device-width: 375px) and (max-device-width: 812px) and (-webkit-min-device-pixel-ratio: 3) and (orientation: landscape) {
      padding: env(safe-area-inset-right) env(safe-area-inset-left);
    }
  }
`

export const ContentCard = styled.div<Partial<Props>>`
  border-radius: 10px;
  box-shadow: 0 3px 15px 2px rgba(0, 0, 0, 0.03);
  background-color: #ffffff;
  width: 100%;
  padding: ${props =>
    props.fullWidth ? '50px 0 30px' : props.fullHeight ? '0 50px 30px' : '50px 50px 30px'};

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    box-shadow: none;
    padding: 0;
  }

  ${props =>
    props.mobileLandscapeSignaturePad &&
    `@media screen and (orientation: landscape) {
    max-height: 100%;
    padding: 0 0 30px 0;
  }`}
`

const AppStoreButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin-top: 30px;
`

const AppStoreButtons = styled.a`
  background: rgb(255, 255, 255);
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.13);
  cursor: pointer;
  display: flex;
  height: 38px;
  justify-content: center;
  align-items: center;
  margin: 0 10px;
  width: 134px;
`

const QueryOverrideDisclosure = styled.div`
  max-width: 800px;
  margin-top: 12px;
  padding: 0 30px;
  font-size: 10px;
  line-height: 1.2;
  text-align: center;
  color: ${style(theme => theme.colors.stone)};
`

const OneTrustContainer = styled.div<{ show: boolean }>`
  display: none;

  @media (min-width: ${style(theme => theme.breakpoints.sm)}px) {
    ${props => props.show && 'display: block;'}
    text-align: center;
    margin-top: 10px;
  }
`

const appStoreButtons: AppStoreButton[] = [
  {
    image: AppStoreButton,
    href: APP_STORE_URL,
    trackerId: TRACKER_IDS.SUCCESS.APP_STORE_BUTTON,
    testId: TestIDs.SUCCESS.APP_STORE,
    altText: 'Apple Store',
  },
  {
    image: GooglePlayButton,
    href: GOOGLE_PLAY_URL,
    trackerId: TRACKER_IDS.SUCCESS.GOOGLE_PLAY_BUTTON,
    testId: TestIDs.SUCCESS.GOOGLE_PLAY,
    altText: 'Google Play',
  },
]

const TrackAppStoreButton = (button, index) => {
  const TrackedAppStoreButton = withClickTrack<any, any>(button.trackerId)(AppStoreButtons)

  return (
    <TrackedAppStoreButton key={index} href={button.href} data-testid={button.testId}>
      <img src={button.image} alt={button.altText} />
    </TrackedAppStoreButton>
  )
}

export const enhance = compose<InnerProps, Props>(
  withRouter,
  withAdBlocker,
  withHeroText,
  withTieredRegistration,
  withState(
    'showToast',
    'setShowToast',
    ({ match, adBlockDetected, pageNotFound }) =>
      pageNotFound ||
      !match.isExact ||
      (adBlockDetected && window.sessionStorage.getItem('ADBLOCK_MESSAGE_DISMISSED') === 'false'),
  ),
  withProps(({ adBlockDetected, hideNavOnMobileLandscape, tierPrice }) => {
    const hideNav = isMobile() && hideNavOnMobileLandscape && window.innerWidth > window.innerHeight
    const showAdBlock =
      window.sessionStorage.getItem('ADBLOCK_MESSAGE_DISMISSED') === 'false' && adBlockDetected
    return {
      showAdBlock,
      hideNav,
      tierPrice,
    }
  }),
  withHandlers({
    handleToastDismiss: ({ setShowToast, showAdBlock, match }) => () => {
      if (showAdBlock && match.isExact) {
        window.sessionStorage.setItem('ADBLOCK_MESSAGE_DISMISSED', 'true')
      }
      setShowToast(false)
    },
  }),
)

export const Layout: React.SFC<InnerProps> = props => (
  <LayoutContainer className={props.className}>
    <Container>
      <ContentCard fullWidth={props.fullWidth} fullHeight={props.fullHeight}>
        {props.children}
      </ContentCard>
    </Container>
    {props.zestyOverride && props.zestyOverride.disclosure_override && (
      <QueryOverrideDisclosure>{props.zestyOverride.disclosure_override}</QueryOverrideDisclosure>
    )}
    {props.showAppStoreButtons ? (
      <AppStoreButtonsContainer>
        {appStoreButtons.map((button, index) => TrackAppStoreButton(button, index))}
      </AppStoreButtonsContainer>
    ) : null}

    {/** We can't simply unmount it. OneTrust will try to read it at the first render, if it's not in the DOM, the eventListeners will never be added. All we can do is hide it. */}
    <OneTrustContainer show={props.showOneTrustLink}>
      <OneTrustLink />
    </OneTrustContainer>

    {props.showToast && (
      <Toast
        adBlock={!props.match.isExact || props.pageNotFound ? false : props.showAdBlock}
        onClose={props.handleToastDismiss}
      />
    )}
  </LayoutContainer>
)

export default enhance(Layout)
